// Turns each programme's certificate record into what the shared TekWissen certificate shows
const pct = (v) => (v || v === 0 ? `${Math.round(Number(v) * 10) / 10}%` : '-');
const num = (v) => (v ?? '-');

export const rpatFoundationCertificate = (c) => ({
  subtitle: 'Certified Recruiter Program',
  recipientName: c.user_name,
  recipientEmail: c.user_email,
  description: [
    'has successfully completed the ', { b: 'Recruitment Process Autonomous Training (R-PAT)' },
    ' program, demonstrating comprehensive knowledge and skills in professional recruitment practices, including sourcing, candidate engagement, interview coordination, negotiation, and client management.',
  ],
  stats: [
    { value: num(c.topics_completed), label: 'Topics Completed' },
    { value: pct(c.final_exam_score), label: 'Final Exam Score' },
    { value: num(c.typing_tests_passed), label: 'Typing Tests Passed' },
    { value: `${c.total_hours_completed ?? 0}h`, label: 'Hours Completed' },
  ],
  credentialId: c.credential_id,
  issuedAt: c.issued_date,
});

export const rpatAdvancedCertificate = (c) => ({
  subtitle: 'TekWissen Certified Recruiter',
  recipientName: c.user_name || c.recipient_name,
  recipientEmail: c.user_email,
  description: [
    'has successfully completed the ', { b: 'R-PAT Advanced Certification' }, ' with specialization in ', { b: c.specialization || 'Recruitment' },
    ', demonstrating expert-level recruitment skills across job mastery, sourcing labs, client role-plays, and the final certification assessment.',
  ],
  stats: [
    { value: pct(c.score), label: 'Final Score' },
    { value: num(c.jobs_mastered), label: 'Jobs Mastered' },
    { value: num(c.labs_completed), label: 'Sourcing Labs Completed' },
    { value: num(c.roleplays_completed), label: 'Role-Plays Completed' },
  ],
  credentialId: c.certificate_number,
  issuedAt: c.issued_at,
});

export const arcCertificate = (c) => ({
  subtitle: 'Advanced Recruitment Certification',
  recipientName: c.user_name,
  recipientEmail: c.user_email,
  description: [
    'has successfully completed the ', { b: 'Advanced Recruitment Certification (ARC)' },
    ' program, demonstrating proficiency in recruiting fundamentals, technical knowledge, candidate sourcing, screening, negotiation, and client management.',
  ],
  stats: [
    { value: num(c.total_chapters), label: 'Chapters Completed' },
    { value: pct(c.final_score), label: 'Final Exam Score' },
    { value: num(c.total_modules), label: 'Modules Completed' },
  ],
  credentialId: c.certificate_id,
  issuedAt: c.completion_date,
});

export const insideSalesCertificate = (c) => ({
  subtitle: 'Inside Sales Certification Program',
  recipientName: c.user_name,
  recipientEmail: c.user_email,
  description: [
    'has successfully completed the ', { b: 'Inside Sales Training' },
    ' program, demonstrating practical skills in the MSP ecosystem, direct client sales strategy, the sales process, and engaging decision-makers.',
  ],
  stats: [
    { value: num(c.sessions_completed), label: 'Sessions Completed' },
    { value: pct(c.final_score), label: 'Average Score' },
    { value: num(c.weeks_completed), label: 'Weeks Completed' },
    { value: `${c.training_hours ?? 0}h`, label: 'Hours Completed' },
  ],
  credentialId: c.certificate_id,
  issuedAt: c.certification_date,
});

const listOf = (items) => {
  const names = items.slice(0, 3).map((t) => (t.length > 45 ? `${t.slice(0, 44)}…` : t));
  if (items.length > 3) return `${names.join(', ')} and more`;
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
};

// Tech Learning, Finance Training and every Course Builder module
export const courseCertificate = (c, viewerEmail) => {
  const program = c.program || 'Tech Learning';
  const modules = (c.module_titles || []).filter((t) => !/^(module|section|chapter|part)\s*\d+$/i.test(t));
  const hasQuiz = (c.quiz_count ?? (c.final_score != null ? 1 : 0)) > 0;
  const score = c.score ?? c.final_score;
  return {
    subtitle: `${program} Program`,
    recipientName: c.user_name,
    recipientEmail: c.user_email || viewerEmail,
    description: [
      'has successfully completed the ', { b: c.course_title }, ` course in the TekWissen ${program} program`,
      modules.length ? ', covering ' : '', modules.length ? { b: listOf(modules) } : '',
      `, completing every lesson${hasQuiz ? ' and passing every assessment' : ''}`,
      c.category ? `${hasQuiz ? ', and' : ' and'} demonstrating practical knowledge and skills in ${c.category}.` : '.',
    ],
    stats: [
      { value: num(c.lessons_completed ?? c.completed_lessons), label: 'Lessons Completed' },
      hasQuiz && { value: pct(score), label: 'Final Score' },
      hasQuiz && { value: num(c.quizzes_passed ?? 1), label: 'Assessments Passed' },
      c.modules_completed != null && { value: c.modules_completed, label: 'Modules Completed' },
      !hasQuiz && { value: '100%', label: 'Course Completion' },
    ].filter(Boolean),
    credentialId: c.certificate_number,
    issuedAt: c.issued_at || c.issue_date,
  };
};
